import { ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import { scrollToSection, useLenis } from "@/components/LenisProvider";

const BASE =
  "https://customer-assets-lqy194kg.emergentagent.net/job_contact-connect-59/artifacts/";

const STEPS = [
  {
    src: "26a18476a992007d_IMG_1857.jpeg",
    title: "Trazo y estructura",
    text: "Marcamos en la pared y armamos la estructura metálica, recta y nivelada.",
  },
  {
    src: "a4fee73e638d0cba_IMG_1858.jpeg",
    title: "Armado de muros",
    text: "Levantamos la estructura de cada muro, pieza por pieza.",
  },
  {
    src: "9f56965d5c929df5_IMG_1856.jpeg",
    title: "Paneles puestos",
    text: "Cerramos con paneles: la cocina empieza a tomar forma.",
  },
  {
    src: "c580deb58f34c672_IMG_1855.jpeg",
    title: "Instalaciones",
    text: "Agua, luz y drenaje en su punto exacto antes de continuar.",
  },
  {
    src: "af179b2c995e8b56_IMG_1852.jpeg",
    title: "Primera base",
    text: "Armamos la base de los muebles, pieza por pieza.",
  },
  {
    src: "416cf08292d7839a_IMG_1850.jpeg",
    title: "Bases en L",
    text: "La distribución completa de tu cocina toma forma.",
  },
  {
    src: "0ab24fd1d1b3e344_IMG_1851.jpeg",
    title: "Todo nivelado",
    text: "Cada base queda nivelada y anclada al muro.",
  },
  {
    src: "8df83b6b4f10af90_IMG_1854.jpeg",
    title: "Bases listas",
    text: "Bases terminadas y firmes, listas para los muebles.",
  },
  {
    src: "7f6bccc9559ed42e_IMG_1848.jpeg",
    title: "Muebles montados",
    text: "Levantamos y montamos muebles bajos y aéreos en su lugar.",
  },
  {
    src: "be6f653d33036e47_IMG_1849.jpeg",
    title: "Aéreos y torre",
    text: "Instalamos los aéreos y la torre despensa a la medida.",
  },
  {
    src: "26ca5d868a81ff77_IMG_1842.jpeg",
    title: "Vista general",
    text: "Toda la cocina montada, lista para los detalles finales.",
  },
  {
    src: "7caeee9db2c58ed9_IMG_1843.jpeg",
    title: "Cubierta instalada",
    text: "Colocamos la cubierta de mármol con su fregadero.",
  },
  {
    src: "52afbebc942fa908_IMG_1844.jpeg",
    title: "Puertas en camino",
    text: "Montamos las puertas con su protección para no rayarlas.",
  },
  {
    src: "a0053474df5ace8c_IMG_1845.jpeg",
    title: "Herrajes y ajustes",
    text: "Ajustamos fregadero, parrilla y cada puerta.",
  },
  {
    src: "8459d27a57227090_IMG_1847.jpeg",
    title: "Casi lista",
    text: "La cocina casi lista para su entrega.",
  },
  {
    src: "03cf35582d77e881_IMG_1838.jpeg",
    title: "Falso techo",
    text: "Armamos la estructura del falso techo sobre tus aéreos.",
  },
  {
    src: "92bd0984c16dd014_IMG_1839.jpeg",
    title: "Frentes montados",
    text: "Puertas y frentes en su lugar, alineados al milímetro.",
  },
  {
    src: "900c8165448c0f86_IMG_1840.jpeg",
    title: "Detalles de esquina",
    text: "Cuidamos cada esquina y cada unión.",
  },
  {
    src: "ab7e064ef101a77a_IMG_1841.jpeg",
    title: "Rumbo a la entrega",
    text: "Últimos detalles antes de entregar tu cocina.",
  },
  {
    src: "16f2facc715b2c81_IMG_1832.jpeg",
    title: "Detalles de brillo",
    text: "Frentes pulidos, luces encendidas, alineación perfecta.",
  },
  {
    src: "cd0b563c68917978_IMG_1831.jpeg",
    title: "Cocina en uso",
    text: "La familia ya cocina en ella: así de viva queda.",
  },
  {
    src: "d64fc987c3c78eb3_IMG_1833.jpeg",
    title: "Todo en su lugar",
    text: "Campana, parrilla y cada rincón trabajan como deben.",
  },
  {
    src: "df18259efdd3fbc0_IMG_1830.jpeg",
    title: "¡Entregada!",
    text: "De cero a completa: así termina cada proyecto de Toscana.",
  },
];

export default function Process() {
  const lenis = useLenis();

  return (
    <section id="proceso" className="py-24 lg:py-32" data-testid="process-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <Reveal>
            <p className="eyebrow">Nuestro proceso</p>
            <h2 className="mt-5 max-w-xl font-serif text-4xl leading-tight tracking-tight text-olive sm:text-5xl lg:text-6xl">
              De cero a tu cocina completa.
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="lg:max-w-sm">
            <p className="leading-relaxed text-olive-mid">
              Así trabajamos en cada proyecto: sin atajos y con todo en orden,
              desde la primera línea en la pared hasta la última gaveta.
            </p>
          </Reveal>
        </div>

        <Reveal y={36}>
          <div
            className="mt-16 grid items-center gap-10 lg:grid-cols-12"
            data-testid="process-video-block"
          >
            <div className="lg:col-span-7">
              <video
                className="aspect-video w-full rounded-3xl bg-olive object-cover"
                src="/antonio-video.mp4"
                poster={BASE + "df18259efdd3fbc0_IMG_1830.jpeg"}
                controls
                playsInline
                preload="metadata"
                data-testid="process-video"
              />
            </div>
            <div className="lg:col-span-5 lg:pl-6">
              <p className="eyebrow">Del propio Antonio</p>
              <h3 className="mt-4 font-serif text-3xl leading-tight text-olive sm:text-4xl">
                Te cuento cómo hicimos esta cocina, de cero a completa.
              </h3>
              <p className="mt-4 leading-relaxed text-olive-mid">
                Este proyecto es el mismo que ves paso a paso abajo: contado
                por quien lo dirige, del primer trazo en la pared a la cocina
                entregada y funcionando.
              </p>
            </div>
          </div>
        </Reveal>

        <div className="mt-16 grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-6">
          {STEPS.map((step, i) => (
            <Reveal key={step.title} delay={i * 0.08} y={24}>
              <figure data-testid={`process-step-${i + 1}`}>
                <div className="relative aspect-square overflow-hidden rounded-2xl">
                  <img
                    src={BASE + step.src}
                    alt={`Paso ${i + 1} — ${step.title}`}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-full bg-olive font-serif text-sm text-cream">
                    {i + 1}
                  </span>
                </div>
                <figcaption className="mt-3">
                  <p className="font-serif text-lg leading-tight text-olive">
                    {step.title}
                  </p>
                  <p className="mt-1 text-xs leading-relaxed text-olive-mid">
                    {step.text}
                  </p>
                </figcaption>
              </figure>
            </Reveal>
          ))}

          <Reveal delay={0.5} y={24}>
            <div
              data-testid="process-cta-card"
              className="flex aspect-square flex-col justify-between rounded-2xl bg-terracotta p-5 text-cream transition-colors duration-300 hover:bg-terracotta-dark"
            >
              <p className="font-serif text-xl leading-snug">
                Y termina en la cocina de tus sueños.
              </p>
              <a
                href="#portafolio"
                data-testid="process-cta-button"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(lenis, "#portafolio");
                }}
                className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em]"
              >
                Ver proyectos
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
